import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { Tooltip } from 'antd';
import RiskDropConfirmationModal from 'components/Modal/RiskDropConfirmationModal';

import { compact, concat, find, flatten, keyBy, maxBy, set, sortBy, uniqBy, values } from 'lodash';
import { Axis } from './RiskScatterFullChart/Axis';
import { PointsCloud } from './RiskScatterFullChart/PointsCloud';
import { StakesPointsCloud } from './RiskScatterFullChart/StakesPointsCloud';
import { getItemAlteredByScenario, isItemInactive } from 'utils/rules';
import { baseMargin, circlePlacementDx, circlePlacementDy, circleSpacer, stakesMargin } from './RiskScatterFullChart.functions';

import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import Weight from 'components/controls/Weight';
import { MiniSlider } from 'components/controls/MiniSlider';

import { StakeholdersPointsCloud } from './RiskScatterFullChart/StakeholdersPointsCloud';
import { getStakeholderScoreForScenarioDetailsColor, itemActiveness } from 'utils/ranking';

import './RiskScatterFullChart.sass';



const adaptStakeholderScore = (stakeholder) => {
	const score = getStakeholderScoreForScenarioDetailsColor(stakeholder);

	if (score === null) return null

	return score.toFixed(0)
}

// Bouton « + » d'ajout par zone (Risques / Ouvertures), rendu en SVG pour
// rester dans le repère du chart. Réutilise le flux de création existant via le
// callback onClick (cf. props.onCreate -> handleCreateRisk de la page Risks).
const ZoneAddButton = ({ x, y, title, onClick, testId }: { x: number; y: number; title: string; onClick: () => void; testId?: string }) => {
	const r = 12;
	return (
		<Tooltip title={title}>
			<g
				className="zone-add-button"
				transform={`translate(${x}, ${y})`}
				style={{ cursor: 'pointer' }}
				data-testid={testId}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					onClick();
				}}
			>
				<circle cx={0} cy={0} r={r} fill="#FFFFFF" fillOpacity={0.18} stroke="#FFFFFFdd" strokeWidth={1.5} />
				<line x1={-5} y1={0} x2={5} y2={0} stroke="#FFFFFF" strokeWidth={2} strokeLinecap="round" />
				<line x1={0} y1={-5} x2={0} y2={5} stroke="#FFFFFF" strokeWidth={2} strokeLinecap="round" />
			</g>
		</Tooltip>
	);
};

export const RiskScatterFullChart = (props: any) => {
	const svgRef = useRef<SVGSVGElement>(null);
	// Track existing link keys to detect new links
	const existingLinkKeysRef = useRef<Set<string>>(new Set());
	const [newLinkKeys, setNewLinkKeys] = React.useState<Set<string>>(new Set());

	const { t, i18n } = useTranslation();
	const { currentThemeId } = useTheme();
	const impactAxisLabel = `${t('models.risk.fields.impact')} →`;
	const likelihoodAxisLabel = `${t('models.risk.fields.likelihood')} →`;
	const { style, likelihoods = [], impacts = [], risks = [], opportunities = [], stakes = [], stakeholders = [], riskCategories = [], scenarios = [] } = props;

	const [hoveredElement, setHoveredElement] = React.useState(null);
	const [xy, setXY] = React.useState([0, 0]);

	// Throttle hover state updates via requestAnimationFrame : sur clusters denses
	// (21 stakeholders qui se superposent depuis l AI gen 2026-06-06), la souris
	// declenche onMouseOver/onMouseLeave plusieurs fois par frame. Chaque setState
	// = full re-render du chart. rAF coalesce a un update par frame max → divise
	// par 10 les renders pendant le survol des clusters.
	const hoverRafRef = useRef<number | null>(null);
	const pendingHoverRef = useRef<{ element: any; xy: number[] } | null>(null);
	const setHoveredThrottled = useCallback((element: any, xyVal: number[] | null = null) => {
		pendingHoverRef.current = { element, xy: xyVal || [0, 0] };
		if (hoverRafRef.current != null) return;
		hoverRafRef.current = requestAnimationFrame(() => {
			hoverRafRef.current = null;
			const p = pendingHoverRef.current;
			if (!p) return;
			setHoveredElement(p.element);
			if (p.xy) setXY(p.xy);
		});
	}, []);
	useEffect(() => () => {
		if (hoverRafRef.current != null) cancelAnimationFrame(hoverRafRef.current);
	}, []);

	// Generate scenarios table data
	const generateScenariosTableData = () => {
		if (!hoveredElement) return [];

		const allScenarios = [
			{ id: null, name: t('models.scenario.baseline') },
			...scenarios
		];

		return allScenarios.map(scenario => {
			const scenarioData = getItemAlteredByScenario(hoveredElement, scenario);
			return {
				scenario,
				impact: scenarioData?.impact || (hoveredElement as any)?.impact || 0,
				likelihood: scenarioData?.likelihood || (hoveredElement as any)?.likelihood || 0
			};
		});
	};

	// Stakes — useMemo evite de recreer un nouvel array a chaque render. Sans ca,
	// alteredStakes/similarStakes ont une nouvelle identite a chaque render,
	// se propage en cascade dans visibleLinks/effects → flicker quand bcp de liens.
	const alteredStakes = useMemo(() => props.showStakes ? sortBy(
		stakes.map((c, index) => ({
			...getItemAlteredByScenario(c, props.scenario),
			index: index
		})
		).filter(i => i.enabled !== false),
		'name'
	) : [], [props.showStakes, stakes, props.scenario]);
	const similarStakes = useMemo(() => {
		const stakesCount = alteredStakes.reduce((acc, stake) => {
			const key = "" + stake.trend
			acc[key] = (acc[key] || 0) + 1;
			return acc;
		}, {});
		return keyBy((alteredStakes || []).map(r => ({
			...r,
			similarCount: stakesCount[r.trend] - 1,
			index: alteredStakes.filter(fr => fr.trend == r.trend)
				.findIndex(d => d.id === r.id)
		})), "id");
	}, [alteredStakes]);

	// Stakeholders
	const alteredStakeholders = useMemo(() => props.showStakeholders ? sortBy(
		stakeholders.map((c, index) => ({
			...getItemAlteredByScenario(c, props.scenario),
			index: index
		})
		).filter(s => s.enabled !== false),
		'name'
	) : [], [props.showStakeholders, stakeholders, props.scenario]);
	const filteredStakeholders = useMemo(() => alteredStakeholders.filter(
		s => adaptStakeholderScore(s) !== null && !isItemInactive(s)
	), [alteredStakeholders]);
	const similarStakeholders = useMemo(() => {
		const stakeholdersCount = filteredStakeholders.reduce((acc, stakeholder) => {
			const key = "" + getStakeholderScoreForScenarioDetailsColor(stakeholder)
			acc[key] = (acc[key] || 0) + 1;
			return acc;
		}, {});
		return keyBy((filteredStakeholders || []).map(r => ({
			...r,
			similarCount: stakeholdersCount[adaptStakeholderScore(r)] - 1,
			index: filteredStakeholders.filter(fr => getStakeholderScoreForScenarioDetailsColor(fr) == getStakeholderScoreForScenarioDetailsColor(r))
				.findIndex(d => d.id === r.id)
		})), "id");
	}, [filteredStakeholders]);

	// Risks
	const filteredRisks = useMemo(
		() => (props.showRisks ? risks.filter(r => r.enabled !== false) : []),
		[props.showRisks, risks]
	);
	const similarRisks = useMemo(() => {
		const risksCount = filteredRisks.reduce((acc, risk) => {
			const key = "" + (risk.impact || risk?.o?.impact) + "-" + risk.likelihood;
			acc[key] = (acc[key] || 0) + 1;
			return acc;
		}, {});
		return keyBy(filteredRisks.map(r => ({
			...r,
			similarCount: risksCount["" + (r.impact || r?.o?.impact) + "-" + r.likelihood] - 1,
			index: filteredRisks.filter(fr => (fr.impact || fr?.o?.impact) === (r.impact || r?.o?.impact) && fr.likelihood === r.likelihood)
				.findIndex(d => d.id === r.id)
		})), "id");
	}, [filteredRisks]);

	const filteredOpportunities = useMemo(
		() => (props.showOpportunities ? opportunities.filter(o => o.enabled !== false) : []),
		[props.showOpportunities, opportunities]
	);
	const similarOpportunities = useMemo(() => {
		const opportunitiesCount = filteredOpportunities.reduce((acc, opportunity) => {
			const key = "" + opportunity.impact + "-" + opportunity.likelihood;
			acc[key] = (acc[key] || 0) + 1;
			return acc;
		}, {});
		return keyBy(filteredOpportunities.map(r => ({
			...r,
			similarCount: opportunitiesCount["" + r.impact + "-" + r.likelihood] - 1,
			index: filteredOpportunities.filter(fr => (fr.impact || fr?.o?.impact) === (r.impact || r?.o?.impact) && fr.likelihood === r.likelihood)
				.findIndex(d => d.id === r.id)
		})), "id");
	}, [filteredOpportunities]);

	const [selectedRisks, setSelectedRisks] = React.useState([]);
	const [selectedOpportunities, setSelectedOpportunities] = React.useState([]);
	const [selectedStakes, setSelectedStakes] = React.useState([]);
	const [selectedStakeholders, setSelectedStakeholders] = React.useState([]);

	// Links transition state - hide links during item transitions
	const [isTransitioning, setIsTransitioning] = React.useState(false);
	const isFirstRenderRef = useRef<boolean>(true);
	const prevScenarioIdRef = useRef<string | null>(null);
	const prevDataSignatureRef = useRef<string>('');
	const transitionCountRef = useRef<number>(0);
	const fallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
	// Fallback duration in case no transitionend events fire (2.1s = CSS transition 1.8s + buffer)
	const FALLBACK_DURATION = 2100;

	// Drag & Drop states
	const [isDragging, setIsDragging] = React.useState(false);
	const [draggedItem, setDraggedItem] = React.useState(null);
	const [dragPosition, setDragPosition] = React.useState({ x: 0, y: 0 });
	const [snapPosition, setSnapPosition] = React.useState({ impact: 1, likelihood: 1 });
	const [cursorPosition, setCursorPosition] = React.useState({ x: 0, y: 0 });

	// Confirmation modal state
	const [showConfirmationModal, setShowConfirmationModal] = React.useState(false);
	const [pendingUpdate, setPendingUpdate] = React.useState<{
		item: any;
		newPosition: { impact: number; likelihood: number };
	} | null>(null);
	const [frozenPosition, setFrozenPosition] = React.useState<{ impact: number; likelihood: number } | null>(null);

	// Seuil de rendu des liens : on compte les liens VISIBLES (après filtres de
	// page et toggles de zones), pas les impacts globaux du projet. Filtrer ou
	// masquer des zones peut donc repasser sous le seuil et réactiver
	// l'affichage. Au-delà du seuil, mode dégradé : seuls les liens de
	// l'élément survolé s'affichent.
	const MAX_LINKS_RENDER = 25;

	// Tous les noeuds candidats aux liens — les similar* dérivent déjà des
	// props filtrées par la page et des toggles show*.
	const allLinkNodes = useMemo(
		() => compact(flatten([values(similarRisks), values(similarOpportunities), props.showStakes && values(similarStakes), values(similarStakeholders)])),
		[props.showStakes, similarRisks, similarOpportunities, similarStakes, similarStakeholders]
	);

	// Arêtes entre noeuds visibles. Même logique d'appariement que le rendu :
	// stakeholder→stake/risk, risk→stake, risk↔openings (n-n via la table de liens).
	const allEdges = useMemo(() => {
		const riskOpeningIds = (r: any): string[] =>
			(r?.openingLinks || []).map((l: any) => l.openingId);

		const edges: { origin: any; target: any; impact: any }[] = [];
		for (const origin of allLinkNodes) {
			const originOpenings = origin.__typename === 'Risk' ? riskOpeningIds(origin) : [];
			for (const target of allLinkNodes) {
				if (origin.id === target.id) continue;
				if (origin.__typename === 'Risk' && target.__typename === 'Risk' && !originOpenings.includes(target.id)) continue;
				let impacts = [];
				if (origin.impactedRisks && target.__typename === 'Risk') impacts = concat(impacts, origin.impactedRisks);
				if (origin.impactedStakes && target.__typename === 'Stake') impacts = concat(impacts, origin.impactedStakes);
				if (origin.__typename === 'Risk' && target.__typename === 'Risk') {
					// Reprend le poids du lien risque<->ouverture pour que l'epaisseur/
					// dash du trait reflete le niveau (sinon strength undefined -> niveau 1). #144
					const ol = (origin.openingLinks || []).find((l: any) => l.openingId === target.id);
					impacts = concat({
						riskId: origin.id,
						opportunityId: target.id,
						strength: ol?.strength
					}, impacts);
				}
				const impact = find(impacts, i => (i.riskId === origin.id || i.stakeholderId === origin.id) && (i.stakeId === target.id || i.riskId === target.id || i.opportunityId === target.id));
				if (impact) edges.push({ origin, target, impact });
			}
		}
		return edges;
	}, [allLinkNodes]);

	const tooManyLinks = allEdges.length > MAX_LINKS_RENDER;

	// Arêtes effectivement rendues : mode normal (≤25) = toutes ; mode dégradé
	// (>25) = uniquement celles incidentes à l'élément survolé.
	const renderedEdges = useMemo(() => {
		if (!props.showAllLinks || props.selection) return [];
		if (!tooManyLinks) return allEdges;
		if (!hoveredElement || !(hoveredElement as any).id) return [];
		const hid = (hoveredElement as any).id;
		return allEdges.filter(e => e.origin.id === hid || e.target.id === hid);
	}, [props.showAllLinks, props.selection, tooManyLinks, allEdges, hoveredElement]);

	// Stable key so the new-link effect doesn't fire on every render — the
	// array gets a new reference each render even when content is unchanged
	// (flicker bug Pierre 2026-06-08).
	const visibleLinkIdsKey = useMemo(
		() => renderedEdges.map(e => `${e.origin.id}-${e.target.id}`).sort().join('|'),
		[renderedEdges]
	);

	// Animations actives dès que des liens sont affichés (≤25, survol ou
	// sélection) — c'est le nombre de liens RENDUS qui fait la perf, plus un
	// total global.
	const linksFewClass = (renderedEdges.length > 0 || props.selection) ? ' links-few' : '';

	// Resize : quand la largeur change (panneau de filtres, fenêtre), les points
	// sont recalculés à de nouvelles coords et la transition `transform` 1.8s les
	// ferait glisser — animation inutile. Le parent lève `props.resizing` (piloté
	// par son ResizeObserver) le temps du resize ; on coupe alors les transitions
	// → repositionnement instantané. Flag d'état (pas de diff en rendu) pour
	// rester correct sous React.StrictMode (double-rendu).
	const resizingClass = props.resizing ? ' resizing' : '';

	const maxLikelihood = likelihoods && parseInt(maxBy(values(likelihoods), 'id')?.id || 3);
	const maxImpact = impacts && parseInt(maxBy(values(impacts), 'id')?.id || 3);

	const margin = { top: 0, right: 0, bottom: 0, left: 0 },
		width = props.width - margin.left - margin.right,
		height = props.height - margin.top - margin.bottom;

	const zonesMargin = 10
	const axisMargin = 40
	const chartMargin = 0.25
	const stakeBandHeight = 124
	const stakeholdersBandHeight = 124
	// Marge en haut de l'aire de tracé : les points à probabilité max ont leur
	// libellé au-dessus d'eux ; sans réserve ils sont rognés par le bord de la
	// carte / chevauchent le mini-titre. On descend le haut de l'échelle Y.
	const topPad = 34

	// Espace reserve par les bandes haut (Enjeux) / bas (Parties prenantes).
	// Si une bande est masquee, le quadrant Risques/Ouvertures s'etend dans son
	// espace : vers le haut (enjeux off) et/ou vers le bas (stakeholders off).
	const topBandSpace = props.showStakes ? stakeBandHeight + zonesMargin : 0
	const bottomBandSpace = props.showStakeholders ? stakeholdersBandHeight + zonesMargin : 0

	// Risques | Ouvertures sont cote a cote. Si une seule des deux zones est
	// affichee, elle prend toute la largeur (l'autre est reduite a 0, invisible).
	// Les points/liens derivent de negative/positiveProperties -> ils se
	// replacent automatiquement.
	const negativeWidth = props.showRisks ? (props.showOpportunities ? (width - zonesMargin) / 2 : width) : 0
	const negativeHeight = height - topBandSpace - bottomBandSpace
	const negativeProperties = {
		width: negativeWidth,
		height: negativeHeight,
		x: d3.scaleLinear()
			.domain([1 - chartMargin, maxImpact + chartMargin])
			.range([axisMargin, negativeWidth]),
		y: d3.scaleLinear()
			.domain([1 - chartMargin, maxLikelihood + chartMargin])
			.range([negativeHeight - axisMargin, topPad]),
		margin: {
			top: topBandSpace,
			right: 0,
			bottom: 0,
			left: 0
		}
	}

	const stakeBandProperties = {
		width,
		height: stakeBandHeight,
		x: d3.scaleLinear()
			.domain([0 - chartMargin * 10, 100 + chartMargin * 10])
			.range([0, width - 2 * axisMargin]),
	}

	const positiveWidth = props.showOpportunities ? (props.showRisks ? (width - zonesMargin) / 2 : width) : 0
	const positiveHeight = height - topBandSpace - bottomBandSpace
	const positiveProperties = {
		width: positiveWidth,
		height: positiveHeight,
		x: d3.scaleLinear()
			.domain([1 - chartMargin, maxImpact + chartMargin])
			.range([0, positiveWidth - axisMargin]),
		y: d3.scaleLinear()
			.domain([1 - chartMargin, maxLikelihood + chartMargin])
			.range([positiveHeight - axisMargin, topPad]),
		margin: {
			top: topBandSpace,
			right: 0,
			bottom: 0,
			left: (props.showRisks && props.showOpportunities) ? negativeWidth + zonesMargin : 0
		}
	}

	const stakeholderBandProperties = {
		width,
		height: stakeBandProperties.height,
		x: d3.scaleLinear()
			.domain([0 - chartMargin * 10, 100 + chartMargin * 10])
			.range([0, width - 2 * axisMargin]),
		margin: {
			top: topBandSpace + negativeHeight + zonesMargin,
			right: 0,
			bottom: 0,
			left: 0
		}
	}

	const selectPoint = (point) => {

		setSelectedStakeholders([]);
		setSelectedStakes([]);
		setSelectedRisks([]);
		setSelectedOpportunities([]);

		// n-n : openings liées à un risque via la table de liens (source unique).
		const pointOpeningIds = (r: any): string[] =>
			(r?.openingLinks || []).map((l: any) => l.openingId);

		if (point && point.__typename === "Risk") {
			// Select all stakes from point.impactedStakes
			if (!point.isOpportunity) {
				setSelectedRisks([point]);
				const openingIds = pointOpeningIds(point);
				setSelectedOpportunities(uniqBy(compact(opportunities.filter(o => openingIds.includes(o.id))), "id"));
			} else {
				setSelectedOpportunities([point]);
				setSelectedRisks(uniqBy(compact(risks.filter(r => pointOpeningIds(r).includes(point.id))), "id"));
			};
			setSelectedStakeholders(uniqBy(
				compact(
					stakeholders
						.filter(s => s.impactedRisks.map(ir => ir.riskId).includes(point.id)
						)
				), "id")
			);
			setSelectedStakes(uniqBy(compact(point.impactedStakes.map(i => similarStakes[i.stakeId])), "id"));
		}

		if (point && point.__typename === "Stake") {
			// Select all risks and opportunities from point.impactedRisks and point.impactedOpportunities
			setSelectedStakes([point]);
			setSelectedRisks(uniqBy(compact(filteredRisks.filter(r => r.impactedStakes.map(ii => ii.stakeId).includes(point.id))), "id"));
			setSelectedOpportunities(uniqBy(compact(filteredOpportunities.filter(r => r.impactedStakes.map(ii => ii.stakeId).includes(point.id))), "id"));
			setSelectedStakeholders(uniqBy(compact(stakeholders.filter(r => r.impactedStakes.map(ii => ii.stakeId).includes(point.id))), "id"));
		}

		if (point && point.__typename === "Stakeholder") {
			setSelectedStakeholders([point]);
			setSelectedRisks(uniqBy(compact(
				filteredRisks.filter(r => point.impactedRisks.map(ir => ir.riskId).includes(r.id))), "id")
			);
			setSelectedOpportunities(uniqBy(compact(
				filteredOpportunities.filter(r => point.impactedRisks.map(ir => ir.riskId).includes(r.id))), "id")
			);
			setSelectedStakes(uniqBy(compact(
				stakes.filter(i => point.impactedStakes.map(ii => ii.stakeId).includes(i.id))), "id")
			);
		}

	}

	useEffect(() => {
		const svg = d3.select(svgRef.current)
	}, []);

	// Add global mouse event listeners for drag & drop
	useEffect(() => {
		if (isDragging) {
			const handleMouseMove = (e: MouseEvent) => handleDragMove(e);
			const handleMouseUp = () => handleDragEnd();

			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);

			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [isDragging, draggedItem, snapPosition]);

	useEffect(() => {
		selectPoint(props.selection);
	}, [props.selection, props.risks, props.opportunities, props.stakes]);

	// Detect transitions (scenario changes or data position changes) to hide links during movement
	const dataSignature = useMemo(() => {
		// Create a signature based on item positions to detect when items need to move
		const risksSig = filteredRisks.map(r => `${r.id}:${r.impact}:${r.likelihood}`).join(',');
		const oppsSig = filteredOpportunities.map(o => `${o.id}:${o.impact}:${o.likelihood}`).join(',');
		const indSig = alteredStakes.map(i => `${i.id}:${i.trend}`).join(',');
		const stakeSig = filteredStakeholders.map(s => `${s.id}:${getStakeholderScoreForScenarioDetailsColor(s)}`).join(',');
		return `${risksSig}|${oppsSig}|${indSig}|${stakeSig}`;
	}, [filteredRisks, filteredOpportunities, alteredStakes, filteredStakeholders]);

	// Count of items that will transition
	const transitioningItemsCount = useMemo(() => {
		return filteredRisks.length + filteredOpportunities.length + alteredStakes.length + filteredStakeholders.length;
	}, [filteredRisks.length, filteredOpportunities.length, alteredStakes.length, filteredStakeholders.length]);

	// Handle transition end callback from SVG
	const handleTransitionEnd = React.useCallback((e: React.TransitionEvent) => {
		// Only react to transform transitions on group elements (items)
		if (e.propertyName === 'transform' && (e.target as HTMLElement).tagName === 'g') {
			transitionCountRef.current += 1;

			// When all items have finished transitioning, show links again
			if (transitionCountRef.current >= transitioningItemsCount && isTransitioning) {
				setIsTransitioning(false);
				transitionCountRef.current = 0;
				// Clear fallback timer since transition completed normally
				if (fallbackTimerRef.current) {
					clearTimeout(fallbackTimerRef.current);
					fallbackTimerRef.current = null;
				}
			}
		}
	}, [transitioningItemsCount, isTransitioning]);

	useEffect(() => {
		const scenarioId = props.scenario?.id ?? null;
		const prevScenarioId = prevScenarioIdRef.current;
		const prevDataSignature = prevDataSignatureRef.current;

		// Skip the first render (initialization) - don't trigger transition on initial load
		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			prevScenarioIdRef.current = scenarioId;
			prevDataSignatureRef.current = dataSignature;
			return;
		}

		// Check if scenario changed or data positions changed
		const scenarioChanged = prevScenarioId !== scenarioId;
		const dataChanged = prevDataSignature !== dataSignature && prevDataSignature !== '';

		if (scenarioChanged || dataChanged) {
			// Clear any existing fallback timer
			if (fallbackTimerRef.current) {
				clearTimeout(fallbackTimerRef.current);
			}

			// Reset transition counter and start transition - hide links
			transitionCountRef.current = 0;
			setIsTransitioning(true);

			// Fallback timer in case transitionend events don't fire
			fallbackTimerRef.current = setTimeout(() => {
				setIsTransitioning(false);
				transitionCountRef.current = 0;
			}, FALLBACK_DURATION);
		}

		// Update refs
		prevScenarioIdRef.current = scenarioId;
		prevDataSignatureRef.current = dataSignature;

		return () => {
			if (fallbackTimerRef.current) {
				clearTimeout(fallbackTimerRef.current);
			}
		};
	}, [props.scenario?.id, dataSignature]);

	// Track new links for animation
	const newLinkTimerRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		const currentLinkKeys = new Set<string>();

		// Collect all current link keys
		if (props.showAllLinks && !props.selection) {
			renderedEdges.forEach(e => {
				currentLinkKeys.add(`all-link-${e.origin.id}-${e.target.id}`);
			});
		}

		// Find new links (keys that weren't in the previous set)
		const newKeys = new Set<string>();
		currentLinkKeys.forEach(key => {
			if (!existingLinkKeysRef.current.has(key)) {
				newKeys.add(key);
			}
		});

		// Update the ref with current keys first
		existingLinkKeysRef.current = currentLinkKeys;

		// Clear previous timer
		if (newLinkTimerRef.current) {
			clearTimeout(newLinkTimerRef.current);
		}

		if (newKeys.size > 0) {
			setNewLinkKeys(newKeys);
			// Clear the "new" status after animation completes
			newLinkTimerRef.current = setTimeout(() => {
				setNewLinkKeys(new Set());
			}, 1000);
		}

		return () => {
			if (newLinkTimerRef.current) {
				clearTimeout(newLinkTimerRef.current);
			}
		};
	// Use the derived ID key (string) instead of the visibleLinks array. The
	// array gets a new reference each render even when content is unchanged,
	// so depending on it caused the effect to fire every render and cancel
	// the .new-link settle timer (see flicker note above).
	}, [visibleLinkIdsKey, props.showAllLinks, props.selection]);

	const stakeBandXY = (point) => {
		if (!point) return null
		// Vérifier si l'indicateur existe dans la collection (peut avoir été filtré si désactivé)
		if (!similarStakes[point.id]) return null

		return [
			axisMargin + stakeBandProperties.x(point.trend) +
			circleSpacer(similarStakes[point.id].similarCount) * (stakeBandProperties.width * stakesMargin) * circlePlacementDx(similarStakes[point.id], similarStakes)
			,
			stakeBandProperties.height / 2 +
			circleSpacer(similarStakes[point.id].similarCount) * (stakeBandProperties.width * stakesMargin) * circlePlacementDy(similarStakes[point.id], similarStakes)

		]
	}

	const stakeholdersBandXY = (point) => {
		if (!point) return null
		// Vérifier si le stakeholder existe dans la collection (peut avoir été filtré si désactivé)
		if (!similarStakeholders[point.id]) return null

		const score = adaptStakeholderScore(point);
		if (score === null) return null;
		const x = stakeholderBandProperties.x(score);
		const similarCount = similarStakeholders[point.id].similarCount;
		const similarStakeholdersList = similarStakeholders[point.id]

		const spaceBase = circleSpacer(similarCount) * (stakeholderBandProperties.width * stakesMargin)
		const result = [
			axisMargin + x +
			spaceBase * circlePlacementDx(similarStakeholdersList, similarStakeholders)
			,
			stakeholderBandProperties.height / 2 +
			stakeholderBandProperties.margin.top +
			spaceBase * circlePlacementDy(similarStakeholdersList, similarStakeholders)
		]

		return result
	}

	const riskXY = (point) => {
		if (!point) return null

		// Use snap position during drag for the dragged item
		let impact = (point as any).impact;
		let likelihood = (point as any).likelihood;

		if (isDragging && draggedItem && (draggedItem as any).id === (point as any).id) {
			impact = snapPosition.impact;
			likelihood = snapPosition.likelihood;
		} else if (showConfirmationModal && pendingUpdate && (pendingUpdate.item as any).id === (point as any).id) {
			// Use frozen position when modal is showing
			impact = frozenPosition?.impact ?? (point as any).impact;
			likelihood = frozenPosition?.likelihood ?? (point as any).likelihood;
		}

		const similarRisk = similarRisks[(point as any).id];
		if (!similarRisk) return null;

		return [
			negativeProperties.x(impact) +
			circleSpacer(similarRisk.similarCount) * circlePlacementDx(similarRisk, similarRisks) * Math.min(negativeProperties.width, negativeProperties.height) * baseMargin,
			negativeProperties.y(likelihood) +
			circleSpacer(similarRisk.similarCount) * circlePlacementDy(similarRisk, similarRisks) * Math.min(negativeProperties.width, negativeProperties.height) * baseMargin + negativeProperties.margin.top
		]
	}

	const opportunityXY = (point) => {
		if (!point) return null

		// Use snap position during drag for the dragged item
		let impact = (point as any).impact;
		let likelihood = (point as any).likelihood;

		if (isDragging && draggedItem && (draggedItem as any).id === (point as any).id) {
			impact = snapPosition.impact;
			likelihood = snapPosition.likelihood;
		} else if (showConfirmationModal && pendingUpdate && (pendingUpdate.item as any).id === (point as any).id) {
			// Use frozen position when modal is showing
			impact = frozenPosition?.impact ?? (point as any).impact;
			likelihood = frozenPosition?.likelihood ?? (point as any).likelihood;
		}

		const similarOpportunity = similarOpportunities[(point as any).id];
		if (!similarOpportunity) return null;

		return [
			positiveProperties.x(impact) +
			circleSpacer(similarOpportunity.similarCount) * circlePlacementDx(similarOpportunity, similarOpportunities) * Math.min(positiveProperties.width, positiveProperties.height) * baseMargin + positiveProperties.margin.left,
			positiveProperties.y(likelihood) +
			circleSpacer(similarOpportunity.similarCount) * circlePlacementDy(similarOpportunity, similarOpportunities) * Math.min(positiveProperties.width, positiveProperties.height) * baseMargin + positiveProperties.margin.top
		]
	}

	// Convertit un point écran (clientX/clientY) en coordonnées du repère interne
	// du SVG (unités du viewBox = props.width/props.height). Depuis l'ajout du
	// viewBox, la taille CSS rendue du SVG peut différer de ses unités internes
	// (mise à l'échelle responsive) : sans ce facteur, le snap du drag/double-clic
	// se décalerait dès que la largeur rendue ≠ props.width.
	const clientToViewBox = (clientX: number, clientY: number): [number, number] => {
		const rect = svgRef.current!.getBoundingClientRect();
		const scaleX = rect.width ? props.width / rect.width : 1;
		const scaleY = rect.height ? props.height / rect.height : 1;
		return [(clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY];
	};

	// Drag & Drop functions
	const calculateSnapPosition = (x: number, y: number, isOpportunity = false) => {
		const properties = isOpportunity ? positiveProperties : negativeProperties;

		// Convert screen coordinates to chart coordinates
		const chartX = x - properties.margin.left;
		const chartY = y - properties.margin.top;

		// Find closest impact and likelihood values (including half values)
		const rawImpact = properties.x.invert(chartX);
		const rawLikelihood = properties.y.invert(chartY);

		// Round to nearest 0.5
		const impact = Math.round(rawImpact * 2) / 2;
		const likelihood = Math.round(rawLikelihood * 2) / 2;

		// Clamp values to valid ranges (1 to max, with 0.5 increments)
		const clampedImpact = Math.max(1, Math.min(maxImpact, impact));
		const clampedLikelihood = Math.max(1, Math.min(maxLikelihood, likelihood));

		return { impact: clampedImpact, likelihood: clampedLikelihood };
	};

	// Double-clic dans une zone vide (Risques / Ouvertures) : crée un item à la
	// position (impact / likelihood) cliquée. Le double-clic ne déclenche pas le
	// drag (seuil 5px / mousedown sur les points uniquement), donc pas de conflit ;
	// la modale de création confirme avant toute persistance. Le handler n'est
	// posé que sur le <rect> de fond, pas sur les points (qui stoppent la
	// propagation via leurs propres handlers).
	const handleZoneDoubleClick = (event: React.MouseEvent, isOpportunity: boolean) => {
		if (!props.onCreate) return;
		event.preventDefault();
		event.stopPropagation();
		if (!svgRef.current) {
			props.onCreate(isOpportunity);
			return;
		}
		const [vx, vy] = clientToViewBox(event.clientX, event.clientY);
		const position = calculateSnapPosition(vx, vy, isOpportunity);
		props.onCreate(isOpportunity, position);
	};

	// Seuil (px) au-dela duquel un mousedown devient un vrai drag. En dessous,
	// c'est un clic -> selection seule, le point ne bouge pas (avant : tout
	// mousedown demarrait le drag et repositionnait le point sur le snap, d'ou
	// le "saut" au clic, surtout en mode etendu). Pierre 2026-06-25.
	const DRAG_THRESHOLD = 5;

	const handleDragStart = (item: any, event: React.MouseEvent) => {
		const startX = event.clientX;
		const startY = event.clientY;

		const beginRealDrag = (e: MouseEvent) => {
			cleanup();
			setIsDragging(true);
			setDraggedItem(item);
			setDragPosition({ x: e.clientX, y: e.clientY });
			setHoveredElement(null);
			if (svgRef.current) {
				const [vx, vy] = clientToViewBox(e.clientX, e.clientY);
				const snap = calculateSnapPosition(vx, vy, item.isOpportunity);
				setSnapPosition(snap);
			}
		};

		const onMove = (e: MouseEvent) => {
			if (Math.hypot(e.clientX - startX, e.clientY - startY) <= DRAG_THRESHOLD) return;
			beginRealDrag(e);
		};
		// mouseup avant le seuil = simple clic : on ne fait rien (la selection est
		// geree par onClick), surtout pas de drag/repositionnement.
		const onUp = () => cleanup();
		const cleanup = () => {
			document.removeEventListener('mousemove', onMove);
			document.removeEventListener('mouseup', onUp);
		};
		document.addEventListener('mousemove', onMove);
		document.addEventListener('mouseup', onUp);
	};

	const handleDragMove = (event: MouseEvent) => {
		if (!isDragging || !draggedItem) return;

		setDragPosition({ x: event.clientX, y: event.clientY });

		// Calculate snap position
		if (svgRef.current) {
			const [x, y] = clientToViewBox(event.clientX, event.clientY);
			setCursorPosition({ x: event.clientX, y: event.clientY });
			const snap = calculateSnapPosition(x, y, (draggedItem as any).isOpportunity);
			setSnapPosition(snap);
		}
	};

	const handleDragEnd = () => {
		if (!isDragging || !draggedItem) return;

		// Check if position has actually changed
		const hasPositionChanged =
			(draggedItem as any).impact !== snapPosition.impact ||
			(draggedItem as any).likelihood !== snapPosition.likelihood;

		if (hasPositionChanged) {
			// Stop dragging immediately when showing modal
			setIsDragging(false);
			setHoveredElement(null);

			// Freeze the position at the drop location
			setFrozenPosition(snapPosition);

			// Show confirmation modal
			setPendingUpdate({
				item: draggedItem,
				newPosition: snapPosition
			});
			setShowConfirmationModal(true);
		} else {
			// No change, just clear drag state
			clearDragState();
		}
	};

	const clearDragState = () => {
		setIsDragging(false);
		setDraggedItem(null);
		setDragPosition({ x: 0, y: 0 });
		setSnapPosition({ impact: 1, likelihood: 1 });
		setCursorPosition({ x: 0, y: 0 });
		setHoveredElement(null);
		selectPoint(null);
		props.onSelect(null);
	};

	const handleConfirmDrop = () => {
		if (pendingUpdate) {
			// Update the item's position
			const updatedItem = {
				...pendingUpdate.item,
				impact: pendingUpdate.newPosition.impact,
				likelihood: pendingUpdate.newPosition.likelihood
			};

			// Call the update function if provided
			if (props.onUpdateItem) {
				props.onUpdateItem(updatedItem);
			}
		}

		// Clear states
		setShowConfirmationModal(false);
		setPendingUpdate(null);
		setFrozenPosition(null);
		// Don't call clearDragState here as dragging is already stopped
		setDraggedItem(null);
		setDragPosition({ x: 0, y: 0 });
		setSnapPosition({ impact: 1, likelihood: 1 });
		setCursorPosition({ x: 0, y: 0 });
		selectPoint(null);
		props.onSelect(null);
	};

	const handleCancelDrop = () => {
		// Clear states without updating
		setShowConfirmationModal(false);
		setPendingUpdate(null);
		setFrozenPosition(null);
		// Don't call clearDragState here as dragging is already stopped
		setDraggedItem(null);
		setDragPosition({ x: 0, y: 0 });
		setSnapPosition({ impact: 1, likelihood: 1 });
		setCursorPosition({ x: 0, y: 0 });
		selectPoint(null);
		props.onSelect(null);
	};

	return (
		<div id="RiskScatterFullChart" className={`animation-fade-in theme-${currentThemeId}${linksFewClass}${resizingClass}`} style={{ ...style, position: "relative", outline: "none", overflow: "hidden" }}>

			<svg
				width={"100%"}
				height={"100%"}
				viewBox={`0 0 ${props.width} ${props.height}`}
				preserveAspectRatio="xMidYMid meet"
				tabIndex={-1}
				focusable="false"
				style={{
					...style || {},
					width: "100%",
					outline: "none",
				}}
				ref={svgRef}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					props.onSelect(null);
				}}
				onTransitionEnd={handleTransitionEnd}
			>
				<defs>
					<radialGradient id="gradientNegative" cx="95%" cy="5%" r="100%" >
						<stop offset="50%" stopColor={"#E89611"} stopOpacity={1} style={{
							transition: "stop-color 0.5s ease-out"
						}} />
						<stop className="color" offset="240%" stopColor="#E89611" stopOpacity={0} />
					</radialGradient>
					<radialGradient id="gradientPositive" cx="95%" cy="5%" r="100%" >
						<stop offset="50%" stopColor={"#4A90D9"} stopOpacity={1} style={{
							transition: "stop-color 0.5s ease-out"
						}} />
						<stop className="color" offset="240%" stopColor="#4A90D9" stopOpacity={0} />
					</radialGradient>
					<linearGradient id="gradientStakeBand" x1="0%" y1="0%" x2="100%" y2="0%">
						<stop offset="0%" stopColor="#E70549" />
						<stop offset="100%" stopColor="#24CCB8" />
					</linearGradient>
					<linearGradient id="gradientInfluence" x1="0%" y1="0%" x2="100%" y2="0%">
						<stop offset="0%" stopColor="#FFA500" />
						<stop offset="50%" stopColor="#FFdd00" />
						<stop offset="100%" stopColor="#FFA500" />
					</linearGradient>
				</defs>

				{props.showStakes && (
					<g transform={`translate(0, 0)`}>
						<rect
							width={stakeBandProperties.width}
							height={stakeBandProperties.height}
							x={0}
							y={0}
							rx={16}
							fill={props.showStakes ? 'url(#gradientStakeBand)' : "#FFFFFF06"}
						></rect>
						<text x={props.width / 2} y={stakeBandProperties.height / 2 + 26} textAnchor={"middle"} style={{ fontSize: "70", fontWeight: "600" }} fill={props.showStakes ? "transparent" : "transparent"}>{t('models.stake.name_plural').toUpperCase()}</text>
						{props.showStakes && (<>
							<circle cx={20} cy={22} r={4} fill="#E70549" stroke="#FFFFFFdd" strokeWidth={1.5} />
							<text className="zone-mini-title" x={32} y={26}>{t('models.stake.name_plural').toUpperCase()}</text>
						</>)}
					</g>
				)}
				<g transform={`translate(0, ${negativeProperties.margin.top})`}>
					<rect
						className="background"
						x={0}
						y={0}
							rx={16}
						width={negativeProperties.width}
						height={negativeProperties.height}
						fill={props.showRisks ? "url(#gradientNegative)" : "#FFFFFF06"}
						style={{
							transition: "fill 1.8s"
						}}
						onDoubleClick={(e) => handleZoneDoubleClick(e, false)}
					></rect>
					<text x={(negativeProperties.width / 2) - 5} y={negativeProperties.y(1 - chartMargin) / 2 + 14} style={{ fontSize: "70", fontWeight: "600" }} fill={props.showRisks ? "transparent" : "transparent"} textAnchor='middle'>{t('models.risk.name_plural').toUpperCase()}</text>
						{props.showRisks && (<>
							<circle cx={20} cy={22} r={4} fill="#E89611" stroke="#FFFFFFdd" strokeWidth={1.5} />
							<text className="zone-mini-title" x={32} y={26}>{t('models.risk.name_plural').toUpperCase()}</text>
						</>)}
				</g>
				<g transform={`translate(${positiveProperties.margin.left}, ${positiveProperties.margin.top})`}>
					<rect
						className="background"
						x={0}
						y={0}
							rx={16}
						width={positiveProperties.width}
						height={positiveProperties.height}
						fill={props.showOpportunities ? "url(#gradientPositive)" : "#FFFFFF06"}
						style={{
							transition: "fill 1.8s"
						}}
						onDoubleClick={(e) => handleZoneDoubleClick(e, true)}
					></rect>
					<text x={(positiveProperties.width / 2) + 5} y={positiveProperties.y(1 - chartMargin) / 2} style={{ fontSize: "40", fontWeight: "600" }} fill={props.showOpportunities ? "transparent" : "transparent"} textAnchor='middle'>{t('models.risk.name_plural_positive').toUpperCase()}</text>
						{props.showOpportunities && (<>
							<circle cx={20} cy={22} r={4} fill="#1E5BFF" stroke="#FFFFFFdd" strokeWidth={1.5} />
							<text className="zone-mini-title" x={32} y={26}>{t('models.risk.name_plural_positive').toUpperCase()}</text>
						</>)}

				</g>

				{props.showStakeholders && (
				<g transform={`translate(${stakeholderBandProperties.margin.left}, ${stakeholderBandProperties.margin.top})`}>
					<rect
						width={stakeholderBandProperties.width}
						height={stakeholderBandProperties.height}
						x={0}
						y={0}
							rx={16}
						fill={props.showStakeholders ? 'url(#gradientStakeBand)' : "#FFFFFF06"}
					></rect> : null
					<text x={(stakeholderBandProperties.width / 2) + 5} y={stakeholderBandProperties.height / 2 + 25} style={{ fontSize: "70", fontWeight: "600" }} fill={props.showStakeholders ? "transparent" : "transparent"} textAnchor='middle'>{t('models.item.name_plural').toUpperCase()}</text>
						{props.showStakeholders && (<>
							<circle cx={20} cy={22} r={4} fill="#24CCB8" stroke="#FFFFFFdd" strokeWidth={1.5} />
							<text className="zone-mini-title" x={32} y={26}>{t('models.item.name_plural').toUpperCase()}</text>
						</>)}
				</g>
				)}


				{
					!props.selection && props.showAllLinks && renderedEdges.map(({ origin, target, impact }) => {
							const isBetweenRisks = origin && target && origin.__typename === "Risk" && target.__typename === "Risk";
							const hasStakeSource = origin && origin.__typename === "Stake";
							const hasStakeholderSource = origin && origin.__typename === "Stakeholder";

							const originPositionFunction = origin.__typename === "Stake" ? stakeBandXY :
								origin.__typename === "Stakeholder" ? stakeholdersBandXY :
									origin.isOpportunity ? opportunityXY :
										riskXY
							const targetPositionFunction = similarStakes[target.id] ? stakeBandXY : similarStakeholders[target.id] ? stakeholdersBandXY : similarOpportunities[target.id] ? opportunityXY : riskXY

							const strength = impact?.strength;



							return origin && target && (() => {
								const originPos = originPositionFunction(origin);
								const targetPos = targetPositionFunction(target);

								// Check if position functions returned valid coordinates
								if (!originPos || !targetPos) return null;

								// Ensure line visibility by adding minimum offset for very close coordinates
								const minOffset = 2; // Minimum pixel offset to keep line visible
								let adjustedOriginPos = [...originPos];
								let adjustedTargetPos = [...targetPos];

								// Check if coordinates are too close (especially for horizontal lines)
								const deltaX = Math.abs(originPos[0] - targetPos[0]);
								const deltaY = Math.abs(originPos[1] - targetPos[1]);

								if (deltaX < minOffset && deltaY < minOffset) {
									// If both coordinates are very close, add a small offset
									adjustedTargetPos[0] += minOffset;
									adjustedTargetPos[1] += minOffset;
								} else if (deltaX < minOffset) {
									// If only X coordinates are close (horizontal line), add X offset
									adjustedTargetPos[0] += minOffset;
								} else if (deltaY < minOffset) {
									// If only Y coordinates are close (vertical line), add Y offset
									adjustedTargetPos[1] += minOffset;
								}

								const linkKey = `all-link-${origin.id}-${target.id}`;
								const isNewLink = newLinkKeys.has(linkKey);
								return (
									<g key={linkKey} className={`link-group ${isNewLink ? 'new-link' : ''} ${isTransitioning ? 'transitioning' : ''}`}>
										<path
											stroke={'#F2682E'}
											className={isBetweenRisks ? 'influence' : (hasStakeSource || hasStakeholderSource) ? "reversed" : ""}
											strokeWidth={[0, 1.5, 2.6, 4][strength || 1]}
											strokeDasharray={[undefined, '2,5', '8,5', undefined][strength || 1]}
											fill="none"
											d={`M${adjustedOriginPos[0]},${adjustedOriginPos[1]} L${adjustedTargetPos[0]},${adjustedTargetPos[1]}`}
										/>
										<path
											stroke={"#00000002"}
											strokeWidth={8}
											fill="none"
											d={`M${adjustedOriginPos[0]},${adjustedOriginPos[1]} L${adjustedTargetPos[0]},${adjustedTargetPos[1]}`}
										/>
									</g>
								);
							})()
					})
				}
				{props.selection && flatten([selectedStakes, selectedRisks, selectedOpportunities, selectedStakeholders]).map((targetObject, index) => {

					if (props.selection && props.selection.id && targetObject && targetObject.id && props.selection.id === targetObject.id) return null;
					if (!targetObject) return null;

					const origin = getItemAlteredByScenario(props.selection.__typename === "Stake" ? similarStakes[props.selection.id] :
						props.selection.__typename === "Stakeholder" ? similarStakeholders[props.selection.id] :
							props.selection.isOpportunity ? similarOpportunities[props.selection.id] :
								similarRisks[props.selection.id], props.scenario
					);
					const originPositionFunction = props.selection.__typename === "Stake" ? stakeBandXY :
						props.selection.__typename === "Stakeholder" ? stakeholdersBandXY :
							props.selection.isOpportunity ? opportunityXY :
								riskXY

					const target = getItemAlteredByScenario(similarStakes[targetObject.id] || similarOpportunities[targetObject.id] || similarRisks[targetObject.id] || similarStakeholders[targetObject.id], props.scenario)
					const targetPositionFunction = similarStakes[targetObject.id] ? stakeBandXY : similarStakeholders[targetObject.id] ? stakeholdersBandXY : similarOpportunities[targetObject.id] ? opportunityXY : riskXY

					if (!origin || !target) return null;
					const impacts = concat(origin.impactedRisks || [], origin.impactedStakes || [], origin.openingLinks || [], target.impactedRisks || [], target.impactedStakes || [], target.openingLinks || [])
					const impact = find(impacts, i => i.riskId === target.id || i.stakeId === target.id || i.stakeholderId === target.id || i.openingId === target.id);

					const isBetweenRisks = origin && target && origin.__typename === "Risk" && target.__typename === "Risk";
					const hasStakeSource = origin && origin.__typename === "Stake";
					const hasStakeholderSource = origin && origin.__typename === "Stakeholder";

					const strength = impact?.strength;

					return origin && target && (() => {
						const originPos = originPositionFunction(origin);
						const targetPos = targetPositionFunction(target);

						// Check if position functions returned valid coordinates
						if (!originPos || !targetPos) return null;

						// Ensure line visibility by adding minimum offset for very close coordinates
						const minOffset = 2; // Minimum pixel offset to keep line visible
						let adjustedOriginPos = [...originPos];
						let adjustedTargetPos = [...targetPos];

						// Check if coordinates are too close (especially for horizontal lines)
						const deltaX = Math.abs(originPos[0] - targetPos[0]);
						const deltaY = Math.abs(originPos[1] - targetPos[1]);

						if (deltaX < minOffset && deltaY < minOffset) {
							// If both coordinates are very close, add a small offset
							adjustedTargetPos[0] += minOffset;
							adjustedTargetPos[1] += minOffset;
						} else if (deltaX < minOffset) {
							// If only X coordinates are close (horizontal line), add X offset
							adjustedTargetPos[0] += minOffset;
						} else if (deltaY < minOffset) {
							// If only Y coordinates are close (vertical line), add Y offset
							adjustedTargetPos[1] += minOffset;
						}

						// Key stable : pas d'index positionnel (qui change si l'ordre du
						// tableau bouge → React remount → animation CSS restart → flicker).
						return (
							<g key={`selection-link-${origin.id}-${target.id}`} className={`link-group ${isTransitioning ? 'transitioning' : ''}`}>
								<path
									stroke={'#F2682E'}
									className={isBetweenRisks ? 'influence' : (hasStakeSource || hasStakeholderSource) ? "reversed" : ""}
									strokeWidth={[0, 1.5, 2.6, 4][strength || 1]}
									strokeDasharray={[undefined, '2,5', '8,5', undefined][strength || 1]}
									fill="none"
									d={`M${adjustedOriginPos[0]},${adjustedOriginPos[1]} L${adjustedTargetPos[0]},${adjustedTargetPos[1]}`}
								/>
								<path
									stroke={"#00000002"}
									strokeWidth={8}
									fill="none"
									d={`M${adjustedOriginPos[0]},${adjustedOriginPos[1]} L${adjustedTargetPos[0]},${adjustedTargetPos[1]}`}
								/>
							</g>
						);
					})()
				})}

				<g transform={`translate(${axisMargin}, 0)`}>
					<g transform={`translate(0, ${stakeBandProperties.height / 2})`}>
						<StakesPointsCloud
							data={alteredStakes}
							width={stakeBandProperties.width - (axisMargin) - negativeProperties.x(3 * chartMargin)}
							{...stakeBandProperties}
							similarItems={similarStakes}
							selectedPoints={selectedStakes}
							isSelectedMode={selectedStakes.length > 0 || selectedRisks.length > 0 || selectedOpportunities.length > 0}
							onHover={(element) => {
								setHoveredThrottled(element, element ? stakeBandXY(element) : null);
							}}
							onClick={(stake) => {
								setSelectedStakes([]);
								setSelectedStakeholders([]);
								setSelectedRisks([]);
								setSelectedOpportunities([]);
								props.onSelect({ ...stake });

							}}
							recentCreations={props.recentCreations}
						>
						</StakesPointsCloud>
					</g>
				</g>

				<g className="stakeholders" transform={`translate(${axisMargin}, ${stakeholderBandProperties.margin.top})`}>
					<g transform={`translate(0, ${stakeholderBandProperties.height / 2})`}>
						<StakeholdersPointsCloud
							data={alteredStakeholders}
							width={stakeholderBandProperties.width}
							{...stakeholderBandProperties}
							similarItems={similarStakeholders}
							selectedPoints={selectedStakeholders}
							isSelectedMode={selectedStakes.length > 0 || selectedRisks.length > 0 || selectedOpportunities.length > 0}
							onHover={(element) => {
								setHoveredThrottled(element, element ? stakeholdersBandXY(element) : null);
							}}
							onClick={(stakeholder) => {
								setSelectedStakes([]);
								setSelectedStakeholders([]);
								setSelectedRisks([]);
								setSelectedOpportunities([]);
								props.onSelect({ ...stakeholder });

							}}
							recentCreations={props.recentCreations}
						>
						</StakeholdersPointsCloud>
					</g>
				</g>

				{props.showRisks ? <g transform={`translate(0, ${negativeProperties.margin.top})`}>
					<Axis
						{...negativeProperties}
						chartMargin={chartMargin}
						axisMargin={axisMargin}
						likelihoods={likelihoods}
						impacts={impacts}
						impactLabel={impactAxisLabel}
						likelihoodLabel={likelihoodAxisLabel}
						key={`${maxLikelihood}-${maxImpact}-${Math.round(props.width)}-${Math.round(negativeProperties.width)}-${Math.round(negativeProperties.height)}-${i18n.language}`}
					>
						<PointsCloud
							width={negativeProperties.width}
							{...negativeProperties}
							data={filteredRisks}
							similarItems={similarRisks}
							likelihoods={likelihoods}
							impacts={impacts}
							isRiskView={true}
							selectedPoints={selectedRisks}
							isSelectedMode={selectedStakes.length > 0 || selectedRisks.length > 0 || selectedOpportunities.length > 0}
							onHover={(element) => {
								if (!isDragging) {
									setHoveredThrottled(element, element ? riskXY(element) : null);
								}
							}}
							onClick={(risk) => {
								setSelectedStakes([]);
								setSelectedStakeholders([]);
								setSelectedRisks([]);
								setSelectedOpportunities([]);
								props.onSelect(risk)
							}}
							onDoubleClick={(risk) => props.onRiskClick && props.onRiskClick(risk)}
							recentCreations={props.recentCreations}
							riskCategories={riskCategories}
							// Drag & Drop props
							isDragging={isDragging}
							draggedItem={draggedItem}
							snapPosition={snapPosition}
							onDragStart={handleDragStart}
							// Confirmation modal props
							showConfirmationModal={showConfirmationModal}
							pendingUpdate={pendingUpdate}
							frozenPosition={frozenPosition}
						></PointsCloud>
					</Axis>
				</g> : null}

				{props.showOpportunities ? <g transform={`translate(${positiveProperties.margin.left}, ${positiveProperties.margin.top})`}>
					<Axis
						{...positiveProperties}
						chartMargin={chartMargin}
						axisMargin={axisMargin}
						likelihoods={likelihoods}
						impacts={impacts}
						inverted={true}
						impactLabel={impactAxisLabel}
						likelihoodLabel={likelihoodAxisLabel}
						key={`${maxLikelihood}-${maxImpact}-inv-${Math.round(props.width)}-${Math.round(positiveProperties.width)}-${Math.round(positiveProperties.height)}-${i18n.language}`}
					>
						<PointsCloud
							width={negativeProperties.width}
							{...positiveProperties}
							data={filteredOpportunities}
							similarItems={similarOpportunities}
							likelihoods={likelihoods}
							impacts={impacts}
							isRiskView={false}
							selectedPoints={selectedOpportunities}
							isSelectedMode={selectedStakes.length > 0 || selectedRisks.length > 0 || selectedOpportunities.length > 0}
							onHover={(element) => {
								if (!isDragging) {
									setHoveredThrottled(element, element ? opportunityXY(element) : null);
								}
							}}
							onClick={(risk) => {
								setSelectedStakes([]);
								setSelectedStakeholders([]);
								setSelectedRisks([]);
								setSelectedOpportunities([]);
								props.onSelect(risk)
							}}
							onDoubleClick={(risk) => props.onRiskClick && props.onRiskClick(risk)}
							recentCreations={props.recentCreations}
							riskCategories={riskCategories}
							// Drag & Drop props
							isDragging={isDragging}
							draggedItem={draggedItem}
							snapPosition={snapPosition}
							onDragStart={handleDragStart}
							// Confirmation modal props
							showConfirmationModal={showConfirmationModal}
							pendingUpdate={pendingUpdate}
							frozenPosition={frozenPosition}
						></PointsCloud>
					</Axis>
				</g > : null}

				{/* Drag & Drop Grid and Guidelines */}
				{isDragging && (
					<>
						{/* Dotted grid for snap positions */}
						<g className="drag-grid">
							{/* Vertical lines - including half values */}
							{Array.from({ length: (maxImpact - 1) * 2 + 1 }, (_, i) => 1 + i * 0.5).map((impact, index) => {
								// Debug: log the impact values
								if (index === 0) console.log('Impact values:', Array.from({ length: (maxImpact - 1) * 2 + 1 }, (_, j) => 1 + j * 0.5));
								const properties = (draggedItem as any)?.isOpportunity ? positiveProperties : negativeProperties;
								const x = properties.x(impact) + properties.margin.left;
								const isHalfValue = impact % 1 !== 0;
								// Debug: log position for half values
								if (isHalfValue && index < 3) console.log(`Half value ${impact} at x=${x}`);
								return (
									<line
										key={`v-${impact}`}
										x1={x}
										y1={properties.margin.top}
										x2={x}
										y2={properties.margin.top + properties.height}
										stroke={isHalfValue ? "#00000055" : "#00000077"}
										strokeWidth={isHalfValue ? "1" : "1.5"}
										strokeDasharray={isHalfValue ? "2,4" : "3,3"}
									/>
								);
							})}
							{/* Horizontal lines - including half values */}
							{Array.from({ length: (maxLikelihood - 1) * 2 + 1 }, (_, i) => 1 + i * 0.5).map((likelihood, index) => {
								// Debug: log the likelihood values
								if (index === 0) console.log('Likelihood values:', Array.from({ length: (maxLikelihood - 1) * 2 + 1 }, (_, j) => 1 + j * 0.5));
								const properties = (draggedItem as any)?.isOpportunity ? positiveProperties : negativeProperties;
								const y = properties.y(likelihood) + properties.margin.top;
								const isHalfValue = likelihood % 1 !== 0;
								return (
									<line
										key={`h-${likelihood}`}
										x1={properties.margin.left}
										y1={y}
										x2={properties.margin.left + properties.width}
										y2={y}
										stroke={isHalfValue ? "#00000055" : "#00000077"}
										strokeWidth={isHalfValue ? "1" : "1.5"}
										strokeDasharray={isHalfValue ? "2,4" : "3,3"}
									/>
								);
							})}
						</g>

						{/* Highlighted snap lines */}
						<g className="snap-lines">
							{(() => {
								const properties = (draggedItem as any)?.isOpportunity ? positiveProperties : negativeProperties;
								const snapX = properties.x(snapPosition.impact) + properties.margin.left;
								const snapY = properties.y(snapPosition.likelihood) + properties.margin.top;
								const isHalfImpact = snapPosition.impact % 1 !== 0;
								const isHalfLikelihood = snapPosition.likelihood % 1 !== 0;

								return (
									<>
										{/* Vertical snap line */}
										<line
											x1={snapX}
											y1={properties.margin.top}
											x2={snapX}
											y2={properties.margin.top + properties.height}
											stroke="#FF6B35"
											strokeWidth="2"
											strokeDasharray="none"
										/>
										{/* Horizontal snap line */}
										<line
											x1={properties.margin.left}
											y1={snapY}
											x2={properties.margin.left + properties.width}
											y2={snapY}
											stroke="#FF6B35"
											strokeWidth="2"
											strokeDasharray="none"
										/>
										{/* Snap center circle */}
										<circle
											cx={snapX}
											cy={snapY}
											r="8"
											fill="none"
											stroke="#FF6B35"
											strokeWidth="2"
										/>
									</>
								);
							})()}
						</g>
					</>
				)}


				{/* Boutons d'ajout rendus en DERNIER -> toujours au-dessus des liens/points (z-order SVG). Pierre #133. */}
				{props.showRisks && props.onCreate && (
					<g transform={`translate(0, ${negativeProperties.margin.top})`}>
						<ZoneAddButton x={negativeProperties.width - 22} y={22} title={t('models.risk.actions.create')} onClick={() => props.onCreate(false)} testId="viz-add-risk" />
					</g>
				)}
				{props.showOpportunities && props.onCreate && (
					<g transform={`translate(${positiveProperties.margin.left}, ${positiveProperties.margin.top})`}>
						<ZoneAddButton x={positiveProperties.width - 22} y={22} title={t('models.risk.actions.create_opening')} onClick={() => props.onCreate(true)} testId="viz-add-opportunity" />
					</g>
				)}

			</svg >

			{/* Drag position indicator */}
			{isDragging && (() => {
				// Get the container's position
				const containerRect = document.getElementById('RiskScatterFullChart')?.getBoundingClientRect();
				if (!containerRect) return null;

				// Calculate position relative to the container
				const relativeX = cursorPosition.x - containerRect.left;
				const relativeY = cursorPosition.y - containerRect.top;

				return (
					<div
						style={{
							position: 'absolute',
							left: relativeX + 25,
							top: relativeY - 50,
							background: 'rgba(0, 0, 0, 0.8)',
							color: 'white',
							padding: '4px 8px',
							borderRadius: '4px',
							fontSize: '12px',
							fontWeight: 'bold',
							pointerEvents: 'none',
							zIndex: 1001,
							whiteSpace: 'nowrap'
						}}
					>
						{t('models.risk.fields.impact')}: {snapPosition.impact} | {t('models.risk.fields.likelihood')}: {snapPosition.likelihood}
					</div>
				);
			})()}

			{
				hoveredElement && !isDragging && <Tooltip className="tooltip" styles={{ root: { zIndex: 1000, minWidth: "400px", maxWidth: "400px" } }} title={hoveredElement?.id && <div>
					<b>{hoveredElement ? hoveredElement.name : null}</b>


					{hoveredElement.__typename === "Risk" ? <>
						<div className="scenarios-table-container">
							<table className="scenarios-table">
								<thead>
									<tr>
										<th className="scenario-column">{t("models.scenario.name")}</th>
										<th className="impact-column">{t("models.risk.fields.impact")}</th>
										<th className="likelihood-column">{t("models.risk.fields.likelihood")}</th>
									</tr>
								</thead>
								<tbody>
									{generateScenariosTableData().map((row, index) => {
										const isActiveScenario = props.scenario?.id === row.scenario?.id ||
											(!props.scenario?.id && !row.scenario?.id);
										return (
											<tr key={row.scenario?.id || 'baseline'} className={isActiveScenario ? 'active-scenario' : ''}>
												<td className="scenario-name">{row.scenario?.name || t('models.scenario.baseline')}</td>
												<td className="impact-value">
													{impacts[parseInt(row.impact)] ?
														`${row.impact} - ${impacts[parseInt(row.impact)]?.name}` :
														row.impact
													}
												</td>
												<td className="likelihood-value">
													{likelihoods[parseInt(row.likelihood)] ?
														`${row.likelihood} - ${likelihoods[parseInt(row.likelihood)]?.name}` :
														row.likelihood
													}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</> :
						hoveredElement.__typename === "Stake" ? <>
							<div className="mt-2 mb-1 nowrap d-flex">
								<span className="mr-1">{t("models.stake.fields.trend")}:</span>
								<MiniSlider readOnly style={{
									width: "250px",
									height: "10px",
									marginTop: "3px"
								}} className="flex-grow-0 mt-05" value={hoveredElement.trend}></MiniSlider>
							</div>
							<div className="mb-1 nowrap d-flex">
								<span className="mr-1">{t("models.stake.fields.weight")}: </span><Weight className="mini">{hoveredElement.weight}</Weight>
							</div>
						</> : <>
							<div className="mt-2 mb-1 nowrap d-flex">
								<ul>
									<li className="EditableListItem">
										<div style={{ width: '110px', textAlign: 'left' }}>
											{t('models.item.engagement')}
										</div>
										<div>
											{hoveredElement?.impact
												? {
													low: t('models.item.levels.low'),
													medium: t('models.item.levels.medium'),
													high: t('models.item.levels.high'),
													undefined: t('models.item.levels.inactive_neutral')
												}[
												itemActiveness(
													hoveredElement?.x,
													hoveredElement?.y
												)
												]
												: null}{' '}
											{!hoveredElement || isItemInactive(hoveredElement)
												? ''
												: hoveredElement?.y > 0
													? t('models.item.levels.positive')
													: t('models.item.levels.negative')}
										</div>
									</li>

									<li className="EditableListItem">
										<div style={{ width: '110px', textAlign: 'left' }}>
											{t('models.item.fields.impact.label')}
										</div>
										<div>
											{hoveredElement?.impact
												? {
													low: t('models.item.fields.impact.level.low'),
													medium: t('models.item.fields.impact.level.medium'),
													high: t('models.item.fields.impact.level.high')
												}[hoveredElement.impact]
												: t('models.item.fields.impact.level.low')}
										</div>
									</li>

									<li className="EditableListItem">
										<div style={{ width: '110px', textAlign: 'left' }}>
											{t('models.item.fields.trend.label')}
										</div>
										<div>
											{hoveredElement?.trend
												? {
													green: t('models.item.fields.trend.level.green'),
													orange: t('models.item.fields.trend.level.orange'),
													neutral: t('models.item.fields.trend.level.neutral'),
													red: t('models.item.fields.trend.level.red')
												}[hoveredElement?.trend]
												: t('models.item.fields.trend.level.neutral')}
										</div>
									</li>
									{/* Pierre 2026-06-08 : description retiree du tooltip
									   stakeholders (debordement avec les desc IA longues,
									   bruit visuel inutile pour l interaction de survol). */}
								</ul>

							</div>
						</>
					}

					< div className='text-center'>
						<small>
							{t('actions.click_to_select')}

							{
								hoveredElement.__typename === "Risk" && (
									<>
										-
										{t('actions.click_to_drag')}
									</>
								)
							}
						</small>
					</div>
				</div>} open={hoveredElement} >
					<div id="Tooltip-root" key={hoveredElement?.id || "empty"}
						style={{ zIndex: 1000, position: "absolute", top: xy ? xy[1] - 10 + "px" : 0, left: xy ? xy[0] + margin.left + "px" : 0 }}>

					</div>
				</Tooltip >
			}

			{/* Confirmation Modal */}
			{showConfirmationModal && pendingUpdate && (
				<RiskDropConfirmationModal
					visible={showConfirmationModal}
					onConfirm={handleConfirmDrop}
					onCancel={handleCancelDrop}
					item={pendingUpdate.item}
					newPosition={pendingUpdate.newPosition}
					scenarioName={(props.scenario as any)?.name || t('models.scenario.baseline')}
					impacts={impacts}
					likelihoods={likelihoods}
				/>
			)}

		</div >
	);
};
